import {Component} from '@theme/component';

/** Shopify is authoritative; the shared queue awaits Appstle's own reward work. */
class TeaDucksCart extends Component {
  pendingChange = null;
  updateTimer = 0;
  updating = false;

  connectedCallback() {
    super.connectedCallback();
    this.listeners?.abort();
    this.listeners = new AbortController();
    document.addEventListener('tea-ducks:cart-settled', (event) => {
      if (event.detail.cart.sections?.[this.dataset.sectionId]) this.renderCart(event.detail.cart);
    }, {signal: this.listeners.signal});
  }

  disconnectedCallback() {
    window.clearTimeout(this.updateTimer);
    this.listeners?.abort();
    super.disconnectedCallback();
  }

  handleQuantityInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    window.clearTimeout(this.updateTimer);
    const quantity = Number(input.value);
    if (!Number.isInteger(quantity) || !input.checkValidity()) return;
    this.pendingChange = this.changeFromElement(input, quantity);
    this.updateTimer = window.setTimeout(() => this.flushPendingChange(), 350);
  }

  handleRemove(event) {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    if (this.updating) return;
    window.clearTimeout(this.updateTimer);
    this.pendingChange = this.changeFromElement(link, 0);
    this.flushPendingChange();
  }

  changeFromElement(element, quantity) {
    return {lineKey: element.dataset.lineKey || '', variantId: element.dataset.variantId || '', sellingPlanId: element.dataset.sellingPlanId || '', quantity};
  }

  async flushPendingChange() {
    if (this.updating || !this.pendingChange) return;
    const change = this.pendingChange;
    this.pendingChange = null;
    if (!change.lineKey) return;
    this.updating = true;
    this.setLoading(true);
    this.setStatus(this.dataset.updatingLabel || '');
    try {
      if (!window.TeaDucksCartSafety) throw new Error('Cart verification is unavailable.');
      const cart = await window.TeaDucksCartSafety.mutate(this.dataset.cartChangeUrl, {
        id: change.lineKey, quantity: change.quantity,
      }, [this.dataset.sectionId, 'cart-drawer-section']);
      if (change.sellingPlanId && change.quantity > 0 && !cart.items.some((line) => {
        return String(line.variant_id) === change.variantId
          && String(line.selling_plan_allocation?.selling_plan?.id || '') === change.sellingPlanId;
      })) throw new Error('The subscription selling plan was not preserved.');
      this.setStatus(this.dataset.updatedLabel || '');
      window.requestAnimationFrame(() => this.restoreFocus(change));
    } catch (error) {
      console.error('[Tea Ducks] Cart verification failed.', error);
      this.setStatus(this.dataset.errorLabel || '', true);
    } finally {
      this.updating = false;
      this.setLoading(false);
      if (this.pendingChange) this.flushPendingChange();
    }
  }

  renderCart(cart) {
    const replacement = new DOMParser().parseFromString(cart.sections[this.dataset.sectionId], 'text/html').querySelector('td-cart');
    if (!replacement) throw new Error('The rendered cart markup was invalid.');
    this.replaceChildren(...replacement.childNodes);
    this.updatedCallback();
    this.setLoading(this.updating);
  }

  restoreFocus(change) {
    const input = [...this.querySelectorAll('[data-cart-quantity]')].find((field) => {
      return field.dataset.variantId === change.variantId && (field.dataset.sellingPlanId || '') === change.sellingPlanId;
    });
    input?.focus({preventScroll: true});
  }

  setLoading(loading) {
    this.setAttribute('aria-busy', String(loading));
    for (const input of this.querySelectorAll('[data-cart-quantity]')) input.disabled = loading;
    for (const link of this.querySelectorAll('[data-cart-remove]')) link.setAttribute('aria-disabled', String(loading));
    for (const button of this.querySelectorAll('[data-cart-checkout]')) {
      button.disabled = loading || button.dataset.purchasesEnabled !== 'true' || window.TeaDucksCartSafety?.state !== 'ready';
    }
  }

  setStatus(message, isError = false) {
    const status = this.querySelector('[data-cart-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    status.toggleAttribute('data-error', isError);
  }
}

if (!customElements.get('td-cart')) customElements.define('td-cart', TeaDucksCart);
